import React, { useEffect, useRef, useState, useCallback } from "react";
import { getGooglePlaces, type GooglePlacesAddressParsed } from "@cpos/shared";
import { useClickOutside } from "@/hooks/useClickOutside";
import { selectClass } from "./styles";

interface Suggestion {
  text: string;
  placesId: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelected: (parsed: GooglePlacesAddressParsed) => void;
  regions?: string[];
  placeholder?: string;
  error?: string;
}

const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelected,
  regions = ["ca"],
  placeholder = "Start typing an address...",
  error,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placesService, setPlacesService] = useState<Awaited<
    ReturnType<typeof getGooglePlaces>
  > | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    if (!apiKey) {
      console.warn(
        "[AddressAutocomplete] VITE_GOOGLE_MAPS_API_KEY is not set — autocomplete disabled",
      );
      return;
    }
    getGooglePlaces({ apiKey })
      .then((service) => {
        if (mounted) setPlacesService(service);
      })
      .catch((err) => {
        console.warn(
          "[AddressAutocomplete] Failed to initialize Google Places:",
          err,
        );
      });
    return () => {
      mounted = false;
    };
  }, []);

  useClickOutside(containerRef, () => setShowDropdown(false));

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!placesService || input.length < 3) {
        setSuggestions([]);
        return;
      }
      try {
        const results = await placesService.getSuggestions(input, regions);
        setSuggestions(results);
        setShowDropdown(true);
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
      }
    },
    [placesService, regions],
  );

  const handleChange = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchSuggestions(val);
    }, 300);
  };

  const selectSuggestion = async (suggestion: Suggestion) => {
    setShowDropdown(false);
    setSuggestions([]);
    onChange(suggestion.text);

    if (!placesService) return;
    try {
      const parsed = await placesService.getParsedAddress(suggestion.placesId);
      if (parsed) onAddressSelected(parsed);
    } catch {
      // Keep the text they selected
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      // eslint-disable-next-line security/detect-object-injection
      const selected = suggestions[activeIndex];
      if (selected) void selectSuggestion(selected);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setShowDropdown(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={selectClass(error)}
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="border-border bg-card absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border shadow-lg">
          {suggestions.map((s, i) => (
            <li
              key={s.placesId}
              onMouseDown={() => {
                void selectSuggestion(s);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                i === activeIndex
                  ? "text-foreground bg-blue-50"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {s.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
